import { PAUSE_TOKEN_RE } from "~/utils/lipSyncPauseTokens.js";

export function parseTaskParams(params) {
  if (!params) return {};
  if (typeof params === "string") {
    try {
      const parsed = JSON.parse(params);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return params;
}

function basenameFromUrl(url) {
  if (!url) return "";
  try {
    const path = new URL(String(url), "https://placeholder.local").pathname;
    const base = path.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(base.split("?")[0]) || "";
  } catch {
    const last = String(url).split("/").pop() || "";
    return decodeURIComponent(last.split("?")[0]) || "";
  }
}

function plainScriptLabel(text) {
  PAUSE_TOKEN_RE.lastIndex = 0;
  return String(text ?? "")
    .replace(PAUSE_TOKEN_RE, "")
    .trim();
}

function dialogueRole(row) {
  const side = String(row?.side || row?.role || "").toLowerCase();
  return side === "right" || side === "r" ? "R" : "L";
}

export function dialogueItemsFromParams(params = {}) {
  const p = parseTaskParams(params);
  const rows = Array.isArray(p.dialogues) ? p.dialogues : [];
  return rows
    .map((row, index) => {
      const audioUrl = String(row?.audio_url || "").trim();
      const text = String(row?.text ?? row?.prompt ?? "").trim();
      const audioName = row?.audio_name || row?.audio_file_name || row?.file_name;
      const role = dialogueRole(row);
      return {
        key: `${index}-${role}-${audioUrl || text}`,
        index: index + 1,
        role,
        side: role === "R" ? "right" : "left",
        type: audioUrl ? "audio" : "text",
        text,
        audioUrl,
        audioLabel: audioName ? String(audioName) : basenameFromUrl(audioUrl) || `Speech ${index + 1} audio`,
        voiceId: row?.voice_id || "",
      };
    })
    .filter((item) => item.text || item.audioUrl);
}

export function dialoguePromptFromParams(params = {}) {
  return dialogueItemsFromParams(params)
    .map((row, index) => {
      const content = row.text || (row.audioUrl ? "[Audio]" : "");
      return content ? `Speech ${index + 1} (${row.role}): ${content}` : "";
    })
    .filter(Boolean)
    .join("\n");
}

export function taskScriptText(params) {
  const p = parseTaskParams(params);
  return String(p.prompt || p.text || dialoguePromptFromParams(p)).trim();
}

/** Lip-sync task driven by uploaded/recorded audio (no script prompt). */
export function isAudioDrivenLipSyncTask(item) {
  if (taskScriptText(item?.params)) return false;
  return Boolean(resolveLipSyncTaskAudioUrl(item));
}

export function resolveLipSyncTaskAudioUrl(item) {
  const params = parseTaskParams(item?.params);
  return (
    params.audio_url ||
    item?.params_audio_oss_path ||
    params.audio_oss_path ||
    ""
  );
}

export function lipSyncTaskAudioDisplayLabel(item) {
  const params = parseTaskParams(item?.params);
  const audioName =
    params.audio_name || params.audio_file_name || params.file_name;
  if (audioName) return String(audioName);
  const fromUrl = basenameFromUrl(resolveLipSyncTaskAudioUrl(item));
  return fromUrl || "";
}

/**
 * My Creations card / preview label: prompt → full plain text (UI truncates with ellipsis);
 * audio input → audio file name.
 * @param {{ task_id?: string; params?: unknown; params_audio_oss_path?: string }} video
 */
export function creationDisplayName(video) {
  const scriptText = taskScriptText(video?.params);

  if (scriptText) {
    const plain = plainScriptLabel(scriptText);
    if (plain) return plain;
  }

  const audioLabel = lipSyncTaskAudioDisplayLabel(video);
  if (audioLabel) return audioLabel;

  const taskId = video?.task_id;
  return taskId ? `video_${taskId}.mp4` : "video.mp4";
}

/** Download filename: `lip-sync_{taskId}.mp4` */
export function lipSyncDownloadFileName(taskId) {
  const id = String(taskId ?? "video")
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `lip-sync_${id || "video"}.mp4`;
}
